// ============================================================
// NuanceRetenue — la matière, UNE fois. 6 octobre 2026 (lot 2, point 11).
// ------------------------------------------------------------
// La matière apparaissait trois fois : un champ « Matière lue sur le
// plan » modifiable, une liste « Nuance retenue », et un bandeau
// « Rattacher » au-dessus de la pièce avec sa propre liste. Trois portes
// pour une valeur, et choisir une nuance RÉÉCRIVAIT la lecture du plan —
// on ne voyait plus ce que le plan disait.
//
// Désormais :
//  - UNE liste, la nuance retenue : c'est la seule porte ;
//  - à côté, EN GRIS, ce que le plan dit (`material_label`), qui ne change
//    plus quand on choisit — c'est une lecture, pas une décision ;
//  - quand le plan porte un code que le référentiel ne connaît pas
//    (« MT12-1101 »), un geste de plus : le RETENIR pour les plans
//    suivants. C'est l'alias du §7 — un dictionnaire, pas un apprentissage
//    du modèle.
// ============================================================

import { useState } from 'react';
import { Select } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import SourceDot from '../../ui/SourceDot.jsx';
import { api } from '../../lib/api.js';

const memeNom = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

export function NuanceRetenue({ piece, materiaux = [], disabled, onChange, onRecharger, source }) {
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const lu = String(piece.material_label ?? '').trim();
  const retenue = materiaux.find((m) => m.id === piece.material_id) ?? null;
  // Le code lu n'est PAS la nuance : on propose de s'en souvenir.
  const aRetenir = Boolean(lu && retenue && !memeNom(lu, retenue.designation));

  async function retenir() {
    setEnvoi(true);
    setErreur(null);
    try {
      await api.post(`/catalogue/materials/${retenue.id}/alias`, { alias: lu });
      await onRecharger?.();
    } catch (err) {
      setErreur(err);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div>
      <Select
        label="Nuance retenue"
        value={piece.material_id ? String(piece.material_id) : ''}
        disabled={disabled}
        options={[
          { value: '', label: 'Non rattachée' },
          ...materiaux.map((m) => ({ value: String(m.id), label: m.designation })),
        ]}
        hint="La densité et le prix suivent la nuance retenue."
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : null;
          const m = materiaux.find((x) => x.id === id) ?? null;
          // `material_label` N'EST PLUS réécrit : c'est ce que dit le plan.
          onChange({ material_id: id, density: m?.density ?? null, price_per_kg: m?.price_per_kg ?? null });
        }}
      />
      <p className="field__hint matiere-lue">
        <SourceDot source={source ?? 'ai'} title="Lu sur le plan" /> Lu sur le plan :{' '}
        {lu ? <strong>{lu}</strong> : 'rien'}
        {lu && !piece.material_id ? ' — rattaché à aucune nuance du référentiel : choisissez-la ci-dessus.' : ''}
      </p>
      {aRetenir && !disabled ? (
        <p className="field__hint">
          <Button size="sm" variant="ghost" disabled={envoi} onClick={retenir}>
            {envoi ? 'Enregistrement…' : `Retenir « ${lu} » = ${retenue.designation} pour les plans suivants`}
          </Button>
        </p>
      ) : null}
      {erreur ? <p className="blocage__erreur" role="alert">{erreur.message}</p> : null}
    </div>
  );
}

export default NuanceRetenue;
